// src/app/services/chat.service.ts
import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import axios from 'axios';

@Injectable({
  providedIn: 'root'
})
export class ChatBotService {
  private PROJECT_ID = 'looka-643f2';
  private LOCATION = 'us-central1';
  // This is the numeric ID of your Index Endpoint
  private INDEX_ENDPOINT_ID = '1917075488841400320';
  // This is the ID of the specific deployment on that endpoint
  private DEPLOYED_INDEX_ID = 'deployed_rag_index_v1'; //

  // This is the special high-performance URL for your endpoint
  private INDEX_ENDPOINT_URL = '2016157618.us-central1-620491122783.vdb.vertexai.goog';

  // !!! IMPORTANT: GET A FRESH TOKEN BEFORE EVERY TEST !!!
  private ACCESS_TOKEN = ''; // LEAVE THIS EMPTY AND PASTE A FRESH TOKEN WHEN THE APP LOADS

  // OpenRouter config for LLM
  private OPENROUTER_API_KEY = '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';
  private MODEL = 'meta-llama/llama-3.3-70b-instruct';

  constructor() {}

  // ------------------------
  // Generate embedding for a query
  // ------------------------
  private generateEmbedding(query: string): Observable<number[]> {
    // CHANGE 2: Use the exact same model we used to build the index
    const modelName = 'text-embedding-004';
    const url = `https://${this.LOCATION}-aiplatform.googleapis.com/v1/projects/${this.PROJECT_ID}/locations/${this.LOCATION}/publishers/google/models/${modelName}:predict`;
    
    return from(axios.post(
      url,
      { instances: [{ content: query }] },
      {
        headers: {
          'Authorization': `Bearer ${this.ACCESS_TOKEN}`,
          'Content-Type': 'application/json'
        }
      }
    )).pipe(
      map(response => response.data.predictions[0].embeddings.values)
    );
  }

  // ------------------------
  // Query Vertex AI Vector Index
  // ------------------------
  private queryVertexIndex(query: string): Observable<any> {
    return this.generateEmbedding(query).pipe(
      switchMap(embedding => {
        // CHANGE 3: Use the special high-performance endpoint URL
        const url = `https://${this.INDEX_ENDPOINT_URL}/v1/projects/${this.PROJECT_ID}/locations/${this.LOCATION}/indexEndpoints/${this.INDEX_ENDPOINT_ID}:findNeighbors`;

        return from(axios.post(
          url,
          {
            // CHANGE 4: The payload structure is different for this endpoint
            deployedIndexId: this.DEPLOYED_INDEX_ID,
            queries: [{
              datapoint: {
                featureVector: embedding
              },
              neighborCount: 3
            }]
          },
          {
            headers: {
              'Authorization': `Bearer ${this.ACCESS_TOKEN}`,
              'Content-Type': 'application/json'
            }
          }
        ));
      }),
      map(response => response.data)
    );
  }

  // ------------------------
  // Ask question (main entry)
  // ------------------------
  ask(question: string, accessToken: string): Observable<{ answer: string }> {
    this.ACCESS_TOKEN = accessToken; // Set the fresh token
    if (!this.ACCESS_TOKEN) {
      throw new Error('ACCESS_TOKEN is required.');
    }

    return this.queryVertexIndex(question).pipe(
      switchMap(results => {
        // Extract top 3 documents from the restricts field
        const docs = results.nearestNeighbors[0].neighbors.map((neighbor: any) => {
             const summaryObj = neighbor.datapoint.restricts.find((r: any) => r.namespace === 'summary');
             return summaryObj ? summaryObj.allow[0] : 'Summary not available';
        });
        const sources = results.nearestNeighbors[0].neighbors.map((neighbor: any) => neighbor.datapoint.datapointId);

        // Build context for LLM
        const context = docs.map((doc, i) => `${doc} (Source: ${sources[i]})`).join('\n\n');

        const prompt = `
You are a helpful assistant. Answer the question below using ONLY the context provided.
Always include the source link for the information you use.

Context:
${context}

Question: ${question}
`;

        // Call OpenRouter API
        return from(axios.post(
          'https://openrouter.ai/api/v1/chat/completions',
          {
            model: this.MODEL,
            messages: [{ role: 'user', content: prompt }]
          },
          {
            headers: {
              'Authorization': `Bearer ${this.OPENROUTER_API_KEY}`,
              'Content-Type': 'application/json'
            }
          }
        ));
      }),
      map(response => ({ answer: response.data.choices[0].message.content }))
    );
  }
}
